import {useMemo} from 'react'
import {Link} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import type {ApexOptions} from 'apexcharts'
import {Tag} from '@/components/ui/tag'
import {Skeleton} from '@/components/ui/skeleton'
import {ApexChart, useChartTokens} from '@/components/ui/apex-chart'
import {ExposureCard} from '@/components/exposure-card'
import {SaludAccidentalidadSection} from '@/components/salud-accidentalidad-section'
import {HappyStateCard} from '@/components/worklist-row'
import {EstadoDeArranque} from '@/components/resumen-arranque'
import {
  ColaTabla,
  CountUp,
  DonutChart,
  Eyebrow,
  RadialGauge,
  TileCard,
  TileVacio,
  type FilaCola,
} from '@/components/resumen-piezas'
import {useBateriaConsolidado, type BateriaConsolidadoTenant} from '@/hooks/bateria/use-bateria-solicitudes'
import {usePlanTrabajo} from '@/hooks/sgsst/plan/use-plan-trabajo'
import type {Capabilities} from '@/hooks/empresa/organizacion/use-businesses'
import type {Dashboard} from '@/hooks/sgsst/resumen/use-dashboard'
import type {Ruta} from '@/hooks/sgsst/resumen/use-ruta'
import type {SgsstState} from '@/hooks/sgsst/evaluacion/use-sgsst'
import {BAND_TONE} from '@/lib/bands'
import {formatShortDate} from '@/lib/risk'
import {cn} from '@/lib/utils'

const CYCLE_ORDER = ['PLANEAR', 'HACER', 'VERIFICAR', 'ACTUAR'] as const

/** Puntaje del art. 27 con su banda del art. 28; el PHVA al lado para que la cifra no sea un
 * veredicto sin explicación. */
function SaludDelSistema({dashboard, businessId}: {dashboard: Dashboard; businessId: string}) {
  const {t} = useTranslation()
  const tokens = useChartTokens()
  const {score, period_year: year, is_closed: cerrado} = dashboard.evaluacion
  const banda = score.band

  const ciclos = useMemo(
    () => CYCLE_ORDER.map((cycle) => score.cycles.find((c) => c.cycle === cycle)).filter((c) => c !== undefined),
    [score]
  )
  const colores = useMemo(
    () => [tokens['chart-planear'], tokens['chart-hacer'], tokens['chart-verificar'], tokens['chart-actuar']],
    [tokens]
  )
  const phvaOptions = useMemo<ApexOptions>(
    () => ({
      chart: {type: 'bar'},
      colors: colores,
      plotOptions: {
        bar: {horizontal: true, distributed: true, borderRadius: 3, borderRadiusApplication: 'end', barHeight: '52%'},
      },
      xaxis: {categories: ciclos.map((c) => c.cycle), max: 100},
      yaxis: {labels: {style: {fontSize: '10px'}}},
      tooltip: {
        y: {
          formatter: (value: number, opts?: {dataPointIndex: number}) => {
            const ciclo = ciclos[opts?.dataPointIndex ?? 0]
            return t('home.lente.phvaTooltip', {
              pct: value,
              earned: ciclo.earned,
              possible: ciclo.possible,
            })
          },
        },
      },
      legend: {show: false},
    }),
    [ciclos, colores, t]
  )
  const phvaSeries = useMemo(
    () => [
      {
        name: t('home.lente.phva'),
        data: ciclos.map((c) => (c.possible === 0 ? 0 : Math.round((c.earned / c.possible) * 100))),
      },
    ],
    [ciclos, t]
  )

  const tono = BAND_TONE[banda]
  const colorMedidor =
    tono === 'red' ? tokens['tone-red-fg'] : tono === 'amber' ? tokens['tone-amber-fg'] : tokens.primary

  return (
    <section className="surface overflow-hidden" data-testid="salud-del-sistema">
      <div className="grid gap-4 p-5 md:grid-cols-[minmax(0,1fr)_minmax(0,1.35fr)]">
        <div className="flex flex-col items-center justify-center">
          <RadialGauge
            value={Math.round(score.total_pct)}
            label={t('home.lente.puntaje', {year})}
            color={colorMedidor}
            testId="lente-score-gauge"
          />
          <div className="mt-1 flex flex-wrap items-center justify-center gap-2">
            <Tag tone={tono}>{t(`sgsst.bands.${banda}.label`)}</Tag>
            {cerrado && <Tag tone="neutral">{t('home.lente.selladoTag')}</Tag>}
          </div>
          <p className="mt-2 text-center text-[13px] text-muted-foreground" data-testid="lente-score-resueltos">
            <CountUp value={score.resolved} /> {t('home.lente.resueltos', {workable: score.workable})}
          </p>
        </div>
        <div className="min-w-0">
          <div className="flex items-start justify-between gap-3">
            <Eyebrow>{t('home.lente.phva')}</Eyebrow>
            <Link
              to={`/businesses/${businessId}/sgsst`}
              className="text-[12px] font-semibold text-primary hover:underline"
              data-testid="lente-ver-evaluacion">
              {t('home.lente.verEvaluacion')} →
            </Link>
          </div>
          <ApexChart type="bar" height={186} options={phvaOptions} series={phvaSeries} testId="lente-phva-chart" />
        </div>
      </div>
    </section>
  )
}

/** Solo lo que nadie más puede hacer por el dueño; cada fila aterriza en el elemento, no en la página. */
function DecisionesQueLoEsperan({
  dashboard,
  state,
  businessId,
  capabilities,
}: {
  dashboard: Dashboard
  state: SgsstState
  businessId: string
  capabilities: Capabilities
}) {
  const {t} = useTranslation()
  const {data: planTrabajo} = usePlanTrabajo(businessId)
  const plan = planTrabajo?.plan ?? null
  const firmaPendiente = !!plan && plan.firmado_representante_at === null && capabilities.sgsst_firmar

  const filas: FilaCola[] = []
  if (firmaPendiente) {
    filas.push({
      clave: 'firmar-plan-trabajo',
      severidad: 'amber',
      que: t('home.decisiones.firmarPlan', {year: plan!.period_year}),
      cta: t('home.decisiones.firmar'),
      to: `/businesses/${businessId}/sgsst/plan-trabajo`,
    })
  }
  const vencida = dashboard.plan_vencida_mas_vieja
  if (vencida) {
    filas.push({
      clave: 'accion-vencida',
      severidad: 'red',
      que: t('home.decisiones.accionVencida', {count: dashboard.plan.vencidas}),
      detalle: vencida.descripcion,
      cuando: t('home.decisiones.vencidaHace', {count: vencida.dias_vencida}),
      cta: t('home.decisiones.verAccion'),
      to: `/businesses/${businessId}/sgsst/plan?accion=${vencida.id}`,
    })
  }
  // El reporte a la ARL del art. 28 tiene fecha límite legal: es decisión del dueño.
  const assessment = state.assessment
  if (
    assessment?.autoevaluacion_completed_at &&
    assessment.snapshot_band &&
    assessment.snapshot_band !== 'aceptable' &&
    !assessment.arl_reported_at &&
    assessment.arl_deadline
  ) {
    filas.push({
      clave: 'arl',
      severidad: 'amber',
      que: t('dashboard.tasks.arl'),
      cuando: t('dashboard.tasks.arlWhen', {date: formatShortDate(assessment.arl_deadline)}),
      cta: t('dashboard.tasks.registerArl'),
      to: `/businesses/${businessId}/sgsst`,
    })
  }

  const urgente = dashboard.incidentes.mas_urgente
  if (urgente) {
    filas.push({
      clave: 'reloj-1401',
      severidad: urgente.dias_restantes < 0 ? 'red' : 'amber',
      que: t(`home.decisiones.reloj1401.${urgente.tipo}`),
      detalle: t('home.decisiones.reloj1401Detalle', {fecha: formatShortDate(urgente.evento_fecha)}),
      cuando:
        urgente.dias_restantes < 0
          ? t('home.decisiones.relojVencido', {count: Math.abs(urgente.dias_restantes)})
          : t('home.decisiones.relojQuedan', {count: urgente.dias_restantes}),
      cta: t('home.decisiones.verIncidente'),
      to: `/businesses/${businessId}/incidentes/${urgente.id}`,
    })
  }

  if (filas.length === 0) {
    return (
      <div data-testid="decisiones-bloque">
        <Eyebrow className="mb-2.5">{t('home.decisiones.eyebrow')}</Eyebrow>
        <HappyStateCard
          title={t('home.decisiones.vacioTitle')}
          body={t('home.decisiones.vacioBody')}
          testId="decisiones-vacio"
        />
      </div>
    )
  }
  return (
    <div data-testid="decisiones-bloque">
      <Eyebrow className="mb-2.5">{t('home.decisiones.eyebrow')}</Eyebrow>
      <ColaTabla filas={filas} testId="decisiones-tabla" estrecha />
    </div>
  )
}

/** Las mismas porciones excluyentes del resumen gerencial, para que no cuenten distinto. */
function TarjetaPlan({dashboard, businessId}: {dashboard: Dashboard; businessId: string}) {
  const {t} = useTranslation()
  const tokens = useChartTokens()
  const {plan} = dashboard
  const vacio = plan.total === 0
  const abiertas = plan.total - plan.cumplidas - plan.vencidas
  const labels = [t('plan.resumen.serie.cumplidas'), t('plan.resumen.serie.vencidas'), t('plan.resumen.serie.alDia')]
  const colores = [tokens['chart-planear'], tokens['tone-red-fg'], tokens['chart-actuar']]
  return (
    <TileCard
      eyebrow={t('home.lente.planEyebrow')}
      testId="lente-plan"
      cta={
        vacio
          ? undefined
          : {label: t('home.lente.verPlan'), to: `/businesses/${businessId}/sgsst/plan`, testId: 'lente-ver-plan'}
      }>
      {vacio ? (
        <TileVacio
          titulo={t('home.lente.planVacio')}
          cta={{label: t('home.lente.planVacioCta'), to: `/businesses/${businessId}/sgsst/plan`}}
          testId="lente-plan-vacio"
        />
      ) : (
        <>
          <DonutChart
            labels={labels}
            values={[plan.cumplidas, plan.vencidas, abiertas]}
            colors={colores}
            totalLabel={t('home.lente.planTotal')}
            testId="lente-plan-donut"
            height={190}
          />
          <p className="mt-1 text-center text-[13px] text-muted-foreground">
            {plan.vencidas > 0 ? (
              <Link
                to={`/businesses/${businessId}/sgsst/plan`}
                className="font-semibold text-tone-red-fg hover:underline"
                data-testid="lente-plan-vencidas">
                {t('home.lente.planVencidas', {count: plan.vencidas})}
              </Link>
            ) : (
              t('home.lente.planSinVencidas')
            )}
          </p>
        </>
      )}
    </TileCard>
  )
}

/** Solo el hecho y la próxima fecha (Res. 2764/2022 art. 5): al empleador solo le llega el
 * consolidado, nunca un resultado por persona ni por dimensión. */
function TarjetaBateria({
  consolidado,
  businessId,
}: {
  consolidado: BateriaConsolidadoTenant | undefined
  businessId: string
}) {
  const {t} = useTranslation()
  const href = `/businesses/${businessId}/bateria`
  return (
    <TileCard
      eyebrow={t('home.lente.bateriaEyebrow')}
      testId="lente-bateria"
      cta={consolidado?.disponible ? {label: t('home.lente.verBateria'), to: href} : undefined}>
      {consolidado?.disponible ? (
        <div>
          <p className="font-mono text-[26px] leading-none font-medium text-primary">
            {consolidado.proxima_aplicacion ? formatShortDate(consolidado.proxima_aplicacion) : '—'}
          </p>
          <p className="mt-2 text-[13px] text-muted-foreground">
            {t('home.lente.bateriaProxima', {
              fecha: consolidado.fecha_aplicacion ? formatShortDate(consolidado.fecha_aplicacion) : '',
            })}
          </p>
        </div>
      ) : (
        <TileVacio
          titulo={t('home.lente.bateriaVacio')}
          cta={{label: t('home.lente.bateriaVacioCta'), to: href}}
          testId="lente-bateria-vacio"
        />
      )}
    </TileCard>
  )
}

/** La lente del dueño; lo operativo vive a propósito en la lente del responsable. */
export function ResumenEstrategico({
  dashboard,
  state,
  businessId,
  capabilities,
  ruta,
}: {
  dashboard: Dashboard
  state: SgsstState
  businessId: string
  capabilities: Capabilities
  ruta: Ruta
}) {
  const {t} = useTranslation()
  const {data: consolidado, isPending: bateriaCargando} = useBateriaConsolidado(businessId, true)
  const planVacio = dashboard.plan.total === 0
  const bateriaVacia = !consolidado?.disponible
  // Con los dos módulos vacíos se funden en «Para arrancar»; uno solo conserva su tarjeta.
  const colapsa = planVacio && bateriaVacia
  return (
    <div className="space-y-4" data-testid="resumen-estrategico">
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)] [&>*]:min-w-0">
        <SaludDelSistema dashboard={dashboard} businessId={businessId} />
        <DecisionesQueLoEsperan
          dashboard={dashboard}
          state={state}
          businessId={businessId}
          capabilities={capabilities}
        />
      </div>
      <div className={cn('grid gap-4 [&>*]:min-w-0', state.exposure && 'lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]')}>
        {state.exposure && <ExposureCard exposure={state.exposure} />}
        {/* Decidir el colapso con el consolidado a medio llegar haría parpadear la sección. */}
        {bateriaCargando ? (
          <Skeleton className="h-72 w-full" />
        ) : (
          <div className="grid content-start gap-4">
            {colapsa ? (
              <EstadoDeArranque ruta={ruta} businessId={businessId} />
            ) : (
              <>
                <TarjetaPlan dashboard={dashboard} businessId={businessId} />
                <TarjetaBateria consolidado={consolidado} businessId={businessId} />
              </>
            )}
          </div>
        )}
      </div>
      <SaludAccidentalidadSection businessId={businessId} />
      {/* La fecha de Bogotá que vino en la respuesta, nunca la del reloj del navegador. */}
      <p className="text-center text-xs text-muted-foreground" data-testid="lente-corte">
        {t('home.lente.corte', {fecha: dashboard.hoy})}
      </p>
    </div>
  )
}
